import React, { useCallback, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { AUTH_COLORS, AUTH_FONT } from "../../config/authConstants";
import { PASSPORT_PHOTO, getCroppedImg } from "./cropImage";

/**
 * Passport-size (35 × 45 mm) profile photo crop dialog (react-easy-crop).
 */
function ProfileCropDialog(props) {
  var open = props.open;
  var imageSrc = props.imageSrc;
  var onClose = props.onClose;
  var onConfirm = props.onConfirm;
  var saving = props.saving;

  var _crop = useState({ x: 0, y: 0 });
  var crop = _crop[0];
  var setCrop = _crop[1];

  var _zoom = useState(1);
  var zoom = _zoom[0];
  var setZoom = _zoom[1];

  var _area = useState(null);
  var croppedAreaPixels = _area[0];
  var setCroppedAreaPixels = _area[1];

  var onCropComplete = useCallback(function (_croppedArea, pixels) {
    setCroppedAreaPixels(pixels);
  }, []);

  function handleCancel() {
    if (saving) {
      return;
    }
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  }

  function handleSave() {
    if (!imageSrc || !croppedAreaPixels || saving) {
      return;
    }
    getCroppedImg(imageSrc, croppedAreaPixels)
      .then(function (dataUrl) {
        onConfirm(dataUrl);
      })
      .catch(function () {
        onConfirm(null);
      });
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          fontFamily: AUTH_FONT,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: AUTH_FONT,
          fontSize: 18,
          fontWeight: 500,
          color: AUTH_COLORS.text,
          pb: 1,
        }}
      >
        Crop profile photo
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography
          sx={{
            fontFamily: AUTH_FONT,
            fontSize: 13,
            color: AUTH_COLORS.textSecondary,
            mb: 2,
          }}
        >
          Passport size (35 × 45 mm). Keep your face centred, drag to reposition and use the slider to zoom.
        </Typography>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 280, sm: 360 },
            bgcolor: "#111",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={PASSPORT_PHOTO.aspect}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          ) : null}
        </Box>
        <Box sx={{ px: 1, mt: 2.5 }}>
          <Typography
            sx={{
              fontFamily: AUTH_FONT,
              fontSize: 13,
              color: AUTH_COLORS.textSecondary,
              mb: 0.5,
            }}
          >
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={function (_e, value) {
              setZoom(value);
            }}
            disabled={saving}
            sx={{
              color: "#050A44",
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleCancel}
          disabled={saving}
          sx={{
            fontFamily: AUTH_FONT,
            textTransform: "none",
            color: AUTH_COLORS.textSecondary,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !croppedAreaPixels}
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : null
          }
          sx={{
            fontFamily: AUTH_FONT,
            textTransform: "none",
            bgcolor: "#050A44",
            borderRadius: "6px",
            px: 2.5,
            "&:hover": { bgcolor: "#040833" },
            "&.Mui-disabled": {
              bgcolor: "#050A44",
              opacity: 0.55,
              color: "#fff",
            },
          }}
        >
          {saving ? "Uploading..." : "Save photo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProfileCropDialog;
